/**
 * EMZI NEXUS V6.4 - SOA Drill V2
 * Adaptive AI-coached sequential question engine
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import * as api from '../../../api/contentStudioV2';
import { useProgressWrapper } from '../../../utils/withProgress';

const SOA_CONFIG_DEFAULTS = {
  passThreshold: 8.0,
  maxAttempts: 3
};

const Step5SOADrill = ({ sessionId, token, sessionData, onComplete, onBack, setError, setLoading }) => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const withProgress = useProgressWrapper();
  
  // Admin-configurable SOA thresholds (loaded from backend; falls back to defaults)
  const [soaConfig, setSoaConfig] = useState(SOA_CONFIG_DEFAULTS);

  // State machine
  const [phase, setPhase] = useState('question'); // 'question' | 'evaluating' | 'feedback' | 'ai_rescue' | 'complete'
  const [currentQ, setCurrentQ] = useState(0); // 0-4
  const [attempt, setAttempt] = useState(1); // 1-3
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [answers, setAnswers] = useState({}); // {0: {text, attempts:[{score,feedback}], bestScore, aiRescueUsed}}
  const [lastEvaluation, setLastEvaluation] = useState(null);
  const [avgScore, setAvgScore] = useState(null);
  const [finalSummary, setFinalSummary] = useState(null);
  const [showRetry, setShowRetry] = useState(false);
  const [aiRescueData, setAiRescueData] = useState(null); // AI Rescue suggestion
  
  // AI Rescue tracking per question
  const [rescueRequired, setRescueRequired] = useState({}); // {qIndex: boolean} - rescue must be accepted
  const [rescueAccepted, setRescueAccepted] = useState({}); // {qIndex: boolean} - rescue was accepted
  const [lastAnswerText, setLastAnswerText] = useState(''); // Track last submitted answer
  
  // Prevent double-submission
  const isSubmittingRef = React.useRef(false);

  // Incomplete questions tracking (from finalize validation)
  const [incompleteQuestions, setIncompleteQuestions] = useState([]); // array of 0-based indices
  // Custom confirm modal state (replaces window.confirm for sandbox safety)
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Get primed SOA data
  const soaPlan = sessionData?.soaPlan;
  const blueprint = soaPlan?.blueprint || [];
  const narrative = sessionData?.narrative || soaPlan?.narrative;
  const riskNotes = sessionData?.riskNotes || soaPlan?.risk_notes || [];

  // Cleanup: Ensure loading is cleared when component unmounts
  useEffect(() => {
    return () => {
      console.log('[SOA Drill] Component unmounting, clearing loading state');
      setLoading(false);
      isSubmittingRef.current = false;
    };
  }, [setLoading]);

  // Load admin-configurable SOA thresholds from backend (once on mount)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getSOASettings(token);
        if (!cancelled && s) {
          setSoaConfig({
            passThreshold: Number(s.min_pass_score ?? SOA_CONFIG_DEFAULTS.passThreshold),
            maxAttempts: Number(s.max_attempts_before_rescue ?? SOA_CONFIG_DEFAULTS.maxAttempts),
          });
        }
      } catch (e) {
        console.warn('[SOA Drill] Failed to load admin SOA settings, using defaults', e);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // A) Store current question index in sessionStorage (for Step 8 back navigation)
  useEffect(() => {
    sessionStorage.setItem(`soa.${sessionId}.lastIndex`, String(currentQ));
    console.log('[SOA Drill] Saved current question index to sessionStorage:', currentQ);
  }, [currentQ, sessionId]);

  // C) Read resume signal and restore question index on mount
  useEffect(() => {
    // Check if we should resume from URL query parameter
    const resumeByQuery = searchParams.get('resume') === 'last';
    
    // Check if resume index passed via location state
    const stateIdx = location.state?.resumeIndex;
    
    // Always try to resume from sessionStorage when component mounts
    const savedInStorage = sessionStorage.getItem(`soa.${sessionId}.lastIndex`);
    
    if (resumeByQuery || stateIdx !== undefined || savedInStorage) {
      // Priority: state > URL > sessionStorage
      const savedIdx = stateIdx !== undefined 
        ? stateIdx 
        : (resumeByQuery && savedInStorage)
        ? Number(savedInStorage)
        : savedInStorage
        ? Number(savedInStorage)
        : 4;
      
      // Clamp to valid range 0-4 (5 questions)
      const resumeIdx = Math.min(Math.max(savedIdx, 0), 4);
      
      setCurrentQ(resumeIdx);
      console.log('[SOA Drill] Resumed at question index:', resumeIdx, 
                  '(from', stateIdx !== undefined ? 'state' : resumeByQuery ? 'URL+storage' : 'sessionStorage', ')');
    }
  }, [sessionId]); // Only run once on mount

  // Debug: Log language and blueprint on mount
  useEffect(() => {
    console.log('[Step5SOADrill] Language:', language);
    console.log('[Step5SOADrill] Blueprint length:', blueprint.length);
    if (blueprint.length > 0) {
      console.log('[Step5SOADrill] First question sample:', {
        q: blueprint[0].q?.substring(0, 50),
        q_en: blueprint[0].q_en?.substring(0, 50),
        q_bm: blueprint[0].q_bm?.substring(0, 50),
        q_urban: blueprint[0].q_urban?.substring(0, 50),
        hasLocalized: !!(blueprint[0].q_en || blueprint[0].q_bm || blueprint[0].q_urban)
      });
    }
  }, [blueprint, language]);

  useEffect(() => {
    if (!soaPlan || blueprint.length === 0) {
      console.log('[Step5SOADrill] Missing SOA Plan data:', { 
        hasSoaPlan: !!soaPlan, 
        blueprintLength: blueprint.length, 
        sessionDataKeys: Object.keys(sessionData || {}),
        soaPlanKeys: Object.keys(soaPlan || {})
      });
      
      // Detailed error message showing what's missing
      let errorDetail = '';
      if (!soaPlan) {
        errorDetail = 'SOA Plan is null/undefined.';
      } else if (!blueprint || blueprint.length === 0) {
        errorDetail = `Blueprint is empty (${blueprint?.length || 0} questions). `;
        if (soaPlan.narrative && soaPlan.risk_notes) {
          errorDetail += 'Narrative and risk notes exist, but questions were not generated. ';
        }
      }
      
      const errorMsg = language === 'bm'
        ? `Step 6 belum lengkap. Sila lengkapkan Step 6 (Review & Prime) dahulu. ${errorDetail}`
        : language === 'urban'
        ? `Step 6 belum complete. Sila complete Step 6 (Review & Prime) dulu. ${errorDetail}`
        : `Step 6 incomplete. Please complete Step 6 (Review & Prime) first. ${errorDetail}`;
      setError(errorMsg);
    } else {
      console.log('[Step5SOADrill] SOA Plan loaded:', { 
        blueprintLength: blueprint.length, 
        hasLocalized: blueprint[0]?.q_en ? 'Yes' : 'No',
        firstQuestion: blueprint[0]?.q?.substring(0, 50)
      });
      
      // Check if questions have localized versions
      const hasLocalized = blueprint[0]?.q_en || blueprint[0]?.q_bm || blueprint[0]?.q_urban;
      if (!hasLocalized) {
        console.warn('[Step5SOADrill] Questions not localized! User needs to re-prime (go back to Step 6)');
        const warningMsg = language === 'bm'
          ? '⚠️ Soalan belum dikemaskini. Sila kembali ke Step 6 dan klik "Prime SOA Questions" untuk dapatkan soalan dalam bahasa yang betul.'
          : language === 'urban'
          ? '⚠️ Questions belum updated. Sila go back ke Step 6 dan click "Prime SOA Questions" untuk get questions dalam bahasa yang correct.'
          : '⚠️ Questions not updated. Please go back to Step 6 and click "Prime SOA Questions" to get questions in the correct language.';
        setError(warningMsg);
      } else {
        setError(null); // Clear error if data is present and localized
      }
    }
  }, [soaPlan, blueprint, setError, language, sessionData]);

  // Calculate average score from answers
  useEffect(() => {
    const scores = Object.values(answers).map(a => a.bestScore).filter(s => s !== null);
    if (scores.length > 0) {
      const avg = scores.reduce((sum, s) => sum + s, 0) / scores.length;
      setAvgScore(Math.round(avg * 10) / 10);
    }
  }, [answers]);

  // Helper function to get localized question text based on user's language preference
  const getLocalizedQuestion = (question) => {
    if (!question) return '';
    
    // Check if localized versions exist
    const hasLocalized = question.q_en || question.q_bm || question.q_urban;
    
    if (!hasLocalized) {
      // Fallback to default question text if no localized versions
      return question.q || '';
    }
    
    // Return appropriate localized version based on user's language preference
    switch (language) {
      case 'bm':
        return question.q_bm || question.q || '';
      case 'urban':
        return question.q_urban || question.q || '';
      case 'en':
      default:
        return question.q_en || question.q || '';
    }
  };

  // Calculate text similarity (simple approach using character overlap)
  const calculateSimilarity = (text1, text2) => {
    if (!text1 || !text2) return 0;
    const t1 = text1.toLowerCase().trim();
    const t2 = text2.toLowerCase().trim();
    if (t1 === t2) return 1.0;
    
    // Calculate character-level similarity
    const len = Math.max(t1.length, t2.length);
    let matches = 0;
    const minLen = Math.min(t1.length, t2.length);
    for (let i = 0; i < minLen; i++) {
      if (t1[i] === t2[i]) matches++;
    }
    return matches / len;
  };

  const handleCheckAnswer = async () => {
    // Prevent double-submission
    if (isSubmittingRef.current) {
      console.log('[SOA Drill] Submission already in progress, ignoring duplicate click');
      return;
    }
    
    if (!currentAnswer.trim()) {
      setError(t('cs.validation.required') || 'Please enter an answer');
      return;
    }

    // Check if AI Rescue is required but not accepted - prevent bypass with same answer
    if (rescueRequired[currentQ] && !rescueAccepted[currentQ]) {
      // Calculate similarity to last answer
      const similarity = calculateSimilarity(currentAnswer, lastAnswerText);
      if (similarity > 0.85) { // 85% similar = likely same answer
        const errorMsg = language === 'bm' 
          ? '❌ Sila terima cadangan AI atau ubah jawapan dengan detail yang lebih jelas.'
          : language === 'urban'
          ? '❌ Sila terima AI suggestion atau ubah jawapan dengan details yang lebih specific.'
          : '❌ Please accept AI suggestion or change your answer with clearer details.';
        setError(errorMsg);
        return;
      }
    }

    setError(null);
    setShowRetry(false);
    isSubmittingRef.current = true;

    try {
      console.log('[SOA Drill] Calling evaluateAnswerV2:', { sessionId, currentQ, attempt, answerLength: currentAnswer.length });
      
      const result = await withProgress(
        {
          label: language === 'bm' ? 'AI Coach sedang menilai' : 'AI Coach is evaluating',
          phases: [
            language === 'bm' ? 'Menganalisis jawapan' : 'Analyzing answer',
            language === 'bm' ? 'Memberi markah' : 'Scoring',
            language === 'bm' ? 'Menghasilkan maklum balas' : 'Generating feedback'
          ],
          canCancel: false,
          timeoutMs: 12000,
          meta: {
            session_id: sessionId,
            question_index: currentQ,
            attempt,
            feature: 'content_studio_step7_evaluate'
          }
        },
        async (signal) => {
          return await api.evaluateAnswerV2(token, sessionId, currentQ, currentAnswer, attempt);
        }
      );
      
      // Store this answer for comparison
      setLastAnswerText(currentAnswer);
      
      console.log('[SOA Drill] Evaluation result:', result);
      
      // Store evaluation
      setLastEvaluation(result);
      
      // Check if AI Rescue triggered
      if (result.ai_rescue && result.ai_rescue.triggered) {
        console.log('[SOA Drill] AI Rescue triggered:', result.ai_rescue);
        setAiRescueData(result.ai_rescue);
        // Mark this question as requiring rescue acceptance
        setRescueRequired(prev => ({ ...prev, [currentQ]: true }));
        setPhase('ai_rescue'); // Show AI Rescue UI
      } else {
        // Normal feedback flow
        setPhase('feedback');
      }
      
      // Update answers
      setAnswers(prev => {
        const qAnswers = prev[currentQ] || { text: '', attempts: [], bestScore: null, aiRescueUsed: false };
        const newAttempts = [...qAnswers.attempts, {
          score: result.score,
          feedback: result.feedback,
          attemptNo: result.attempt_no
        }];
        const bestScore = Math.max(...newAttempts.map(a => a.score));
        
        return {
          ...prev,
          [currentQ]: {
            text: currentAnswer,
            attempts: newAttempts,
            bestScore,
            aiRescueUsed: qAnswers.aiRescueUsed // Preserve flag
          }
        };
      });
      
    } catch (err) {
      console.error('[SOA Drill] Error:', err);
      console.error('[SOA Drill] Error details:', { message: err.message, stack: err.stack });
      
      // Check if it's a "SOA Prime not found" error
      if (err.message && err.message.includes('SOA Prime not found')) {
        const errorMsg = language === 'bm'
          ? '❌ Data Step 6 hilang. Sila kembali ke Step 6 dan klik "Prime SOA Questions" semula.'
          : language === 'urban'
          ? '❌ Step 6 data missing. Sila go back ke Step 6 dan click "Prime SOA Questions" lagi.'
          : '❌ Step 6 data missing. Please go back to Step 6 and click "Prime SOA Questions" again.';
        setError(errorMsg);
      } else if (err.message && (err.message.includes('401') || err.message.includes('authentication'))) {
        const errorMsg = language === 'bm'
          ? '🔐 Session tamat tempoh. Sila refresh page dan login semula.'
          : language === 'urban'
          ? '🔐 Session expired. Sila refresh page dan login kembali.'
          : '🔐 Session expired. Please refresh and login again.';
        setError(errorMsg);
      } else {
        const errorMsg = language === 'bm'
          ? err.message || 'Gagal menilai jawapan. Cuba lagi.'
          : language === 'urban'
          ? err.message || 'Failed to evaluate answer. Cuba lagi.'
          : err.message || 'Failed to evaluate answer';
        setError(errorMsg);
      }
      setShowRetry(true);
      setPhase('question'); // Go back to question on error
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleRefine = () => {
    setAttempt(attempt + 1);
    setPhase('question');
    setLastEvaluation(null);
    // Keep current answer for user to edit
  };

  const handleAcceptAIRescue = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('[SOA Drill] Accepting AI Rescue for Q', currentQ);
      console.log('[SOA Drill] AI Rescue Data:', aiRescueData);
      
      // Extract AI Rescue answer with fallback (supports both old and new format)
      const aiAnswerText = aiRescueData.enhanced_answer || aiRescueData.ai_rescue_answer || aiRescueData.suggested_answer || '';
      
      // Pass the AI answer text to the backend to save it
      await api.acceptAIRescue(token, sessionId, currentQ, aiAnswerText);
      
      // Mark rescue as accepted for this question
      setRescueAccepted(prev => ({ ...prev, [currentQ]: true }));
      
      if (!aiAnswerText) {
        console.error('[SOA Drill] AI Rescue answer text is missing!', aiRescueData);
      }
      
      console.log('[SOA Drill] Storing AI Rescue answer:', aiAnswerText);
      
      // Update answers with AI Rescue answer text, adjusted score, and flag
      const updatedAnswers = {
        ...answers,
        [currentQ]: {
          ...answers[currentQ],
          text: aiAnswerText, // Replace with AI Rescue answer
          bestScore: aiRescueData.adjusted_score, // Use adjusted score with penalty
          aiRescueUsed: true,
          originalUserAnswer: answers[currentQ].text // Store original for reference
        }
      };
      
      setAnswers(updatedAnswers);
      console.log('[SOA Drill] Updated answers after AI Rescue:', updatedAnswers);
      
      // Clear AI Rescue state and proceed to next question
      setAiRescueData(null);
      handleNextQuestion();
      
    } catch (err) {
      console.error('[SOA Drill] Accept AI Rescue error:', err);
      setError(err.message || 'Failed to accept AI Rescue');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectAIRescue = () => {
    // Rescue is now REQUIRED - user cannot reject, this is just for closing modal
    console.log('[SOA Drill] User cannot reject AI Rescue - locked at 3 attempts');
    const errorMsg = language === 'bm'
      ? '⚠️ Untuk teruskan, sila terima cadangan AI terlebih dahulu.'
      : language === 'urban'
      ? '⚠️ Untuk proceed, sila terima AI suggestion terlebih dahulu.'
      : '⚠️ To continue, please accept the AI suggestion first.';
    setError(errorMsg);
    // Keep AI Rescue modal visible
  };

  // Navigate to a specific question by index (0-based)
  const navigateToQuestion = (targetIdx) => {
    const clampedIdx = Math.min(Math.max(targetIdx, 0), 4);
    console.log('[SOA Drill] Navigating to Q', clampedIdx);
    setCurrentQ(clampedIdx);
    setCurrentAnswer(answers[clampedIdx]?.text || '');
    setAttempt((answers[clampedIdx]?.attempts?.length || 0) + 1);
    setPhase('question');
    setLastEvaluation(null);
    setAiRescueData(null);
    setError(null);
    setIncompleteQuestions([]); // Clear incomplete markers on navigation
  };

  // Scoped back navigation - goes to previous question, not previous step
  const handleScopedBack = () => {
    if (currentQ > 0) {
      navigateToQuestion(currentQ - 1);
    } else {
      // At Q0 - show custom confirmation to exit to Step 6 (no window.confirm)
      setShowExitConfirm(true);
    }
  };

  const handleNextQuestion = () => {
    if (currentQ < 4) {
      setCurrentQ(currentQ + 1);
      setAttempt(1);
      setCurrentAnswer('');
      setPhase('question');
      setLastEvaluation(null);
    } else {
      // All questions answered
      handleFinalize();
    }
  };

  const handleFinalize = async () => {
    setPhase('finalizing');
    setError(null);

    try {
      const result = await api.finalizeSOADrillV2(token, sessionId, false);
      setFinalSummary(result);
      
      // Get the latest answers from state (use callback to ensure we have latest)
      setAnswers(currentAnswers => {
        // Save answers to sessionData for review page
        const updatedData = {
          soa_answers: currentAnswers,
          soaSummaryV2: result,
          avg_score: avgScore,
          completed_at: new Date().toISOString()
        };
        
        console.log('[SOA Drill] All questions completed, navigating to review page');
        console.log('[SOA Drill] Final Answers:', currentAnswers);
        
        // Navigate to review page (Step 7.5) instead of completing
        setPhase('review');
        
        onComplete({
          ...updatedData,
          showReview: true // Signal to parent to show review page
        });
        
        return currentAnswers; // Return unchanged to maintain state
      });
      
    } catch (err) {
      console.error('[SOA Drill] Finalize error:', err);
      
      // Parse incomplete question numbers from error message
      const incompleteMatch = err.message?.match(/Incomplete questions?:\s*([\d,\s]+)/i)
        || err.message?.match(/Soalan yang belum lengkap:\s*([\d,\s]+)/i);
      if (incompleteMatch) {
        const nums = incompleteMatch[1].split(',').map(s => parseInt(s.trim(), 10) - 1).filter(n => !isNaN(n));
        setIncompleteQuestions(nums);
        console.log('[SOA Drill] Incomplete questions (0-indexed):', nums);
      }
      
      setError(err.message || 'Failed to finalize drill');
      setPhase('feedback'); // Go back to last state
    }
  };

  const handleProceedAnyway = async () => {
    setPhase('finalizing');
    
    try {
      const result = await api.finalizeSOADrillV2(token, sessionId, true);
      setFinalSummary(result);
      
      const updatedData = {
        soa_answers: answers,
        soaSummaryV2: result,
        avg_score: avgScore,
        proceededAnyway: true,
        completed_at: new Date().toISOString()
      };
      
      console.log('[SOA Drill] Proceeding anyway, navigating to review page');
      
      setPhase('review');
      
      onComplete({
        ...updatedData,
        showReview: true
      });
    } catch (err) {
      console.error('[SOA Drill] Proceed anyway error:', err);
      setError(err.message);
      setPhase('complete');
    } finally {
      setLoading(false);
    }
  };

  // Guard: No soaPlan
  if (!soaPlan || blueprint.length === 0) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px' }}>
        <h2 style={{ fontSize: '20px', color: '#D32F2F', marginBottom: '16px' }}>
          {t('cs.steps.soa.incomplete.title')}
        </h2>
        <div style={{ padding: '16px', background: '#FFEBEE', borderRadius: '8px', marginBottom: '16px' }}>
          <p style={{ fontSize: '14px', color: '#C62828', margin: 0 }}>
            {t('cs.steps.soa.incomplete.message')}
          </p>
        </div>
        <button onClick={onBack} style={{ 
          width: '100%', padding: '14px', background: '#0e67a2', border: 'none', 
          borderRadius: '12px', color: '#fff', fontSize: '15px', fontWeight: 600, cursor: 'pointer' 
        }}>
          {t('cs.steps.soa.incomplete.goBack')}
        </button>
      </div>
    );
  }

  // Final Summary Screen
  if (phase === 'complete' && finalSummary) {
    const passed = finalSummary.avg_score >= soaConfig.passThreshold;
    
    return (
      <div style={{ 
        background: '#fff', 
        borderRadius: '10px', // Reduced from 12px
        padding: '14px', // Reduced from 20px
        paddingBottom: `calc(80px + env(safe-area-inset-bottom, 80px))`,
        marginBottom: 'calc(20px + env(safe-area-inset-bottom, 20px))'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>
            {passed ? '🎉' : '✅'}
          </div>
          <h2 style={{ fontSize: '24px', color: '#333', marginBottom: '8px' }}>
            {passed ? (language === 'bm' ? 'Tahniah!' : 'Congratulations!') : (language === 'bm' ? 'Selesai!' : 'Completed!')}
          </h2>
          <p style={{ fontSize: '14px', color: '#666' }}>
            {language === 'bm' ? 'SOA Drill siap' : 'SOA Drill Complete'}
          </p>
        </div>

        {/* Score Summary */}
        <div style={{ 
          padding: '20px', 
          background: passed ? '#E8F5E9' : '#FFF9C4', 
          borderRadius: '12px', 
          marginBottom: '20px',
          border: `2px solid ${passed ? '#4CAF50' : '#FBC02D'}`
        }}>
          <div style={{ fontSize: '32px', fontWeight: 700, color: '#333', textAlign: 'center', marginBottom: '8px' }}>
            {finalSummary.avg_score.toFixed(1)} / 10
          </div>
          <div style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '16px' }}>
            {language === 'bm' ? 'Purata Skor' : 'Average Score'}
          </div>
          
          {/* Per Question */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
            {finalSummary.per_question.map((q, idx) => (
              <div key={`item-${idx}`} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#999', marginBottom: '4px' }}>Q{idx + 1}</div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 600,
                  color: q.best_score >= soaConfig.passThreshold ? '#4CAF50' : q.best_score >= (soaConfig.passThreshold * 0.75) ? '#F7941E' : '#E53935'
                }}>
                  {q.best_score.toFixed(1)}
                </div>
                <div style={{ fontSize: '10px', color: '#999' }}>
                  {q.attempts} {language === 'bm' ? 'cuba' : 'tries'}
                </div>
              </div>
            ))}
          </div>

          {/* Brain Points */}
          <div style={{ 
            padding: '12px', 
            background: '#fff', 
            borderRadius: '8px', 
            textAlign: 'center',
            border: '1px solid #E0E0E0'
          }}>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
              🧠 {language === 'bm' ? 'Brain Points Diperoleh' : 'Brain Points Earned'}
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#F7941E' }}>
              +{finalSummary.bp_awarded} BP
            </div>
          </div>
        </div>

        {/* Proceed Button */}
        {passed && (
          <button 
            onClick={() => onComplete({ soaSummaryV2: finalSummary })}
            style={{ 
              width: '100%', 
              padding: '16px', 
              background: '#4CAF50', 
              border: 'none', 
              borderRadius: '12px', 
              color: '#fff', 
              fontSize: '16px', 
              fontWeight: 600, 
              cursor: 'pointer',
              marginBottom: '12px'
            }}
          >
            {language === 'bm' ? 'Teruskan ke Langkah Seterusnya' : 'Continue to Next Step'}
          </button>
        )}

        {/* Proceed Anyway (if not passed) */}
        {!passed && !finalSummary.proceeded_anyway && (
          <div style={{ marginTop: '16px', padding: '16px', background: '#FFF3E0', borderRadius: '8px', border: '1px solid #F7941E' }}>
            <p style={{ fontSize: '13px', color: '#E65100', marginBottom: '12px' }}>
              {language === 'bm' 
                ? `⚠️ Skor purata di bawah ${soaConfig.passThreshold.toFixed(1)}. Anda boleh teruskan atau balik untuk perbaiki jawapan.`
                : `⚠️ Average score below ${soaConfig.passThreshold.toFixed(1)}. You can proceed anyway or go back to refine answers.`}
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={onBack} style={{ 
                flex: 1, padding: '12px', background: '#fff', border: '2px solid #0e67a2', 
                borderRadius: '8px', color: '#0e67a2', fontSize: '14px', fontWeight: 600, cursor: 'pointer' 
              }}>
                {language === 'bm' ? 'Kembali' : 'Go Back'}
              </button>
              <button onClick={handleProceedAnyway} style={{ 
                flex: 1, padding: '12px', background: '#F7941E', border: 'none', 
                borderRadius: '8px', color: '#fff', fontSize: '14px', fontWeight: 600, cursor: 'pointer' 
              }}>
                {language === 'bm' ? 'Teruskan Juga' : 'Proceed Anyway'}
              </button>
            </div>
          </div>
        )}

        {finalSummary.proceeded_anyway && (
          <button 
            onClick={() => onComplete({ soaSummaryV2: finalSummary, proceededAnyway: true })}
            style={{ 
              width: '100%', 
              padding: '16px', 
              background: '#F7941E', 
              border: 'none', 
              borderRadius: '12px', 
              color: '#fff', 
              fontSize: '16px', 
              fontWeight: 600, 
              cursor: 'pointer'
            }}
          >
            {language === 'bm' ? 'Teruskan' : 'Continue'}
          </button>
        )}
      </div>
    );
  }

  // Main Question Flow
  const question = blueprint[currentQ];
  
  // Helper: check if a question is considered "complete" (score >= 8 or AI rescue accepted)
  const isQuestionComplete = (idx) => {
    const a = answers[idx];
    if (!a) return false;
    if (a.bestScore >= soaConfig.passThreshold) return true;
    if (a.aiRescueUsed) return true;
    if (rescueAccepted[idx]) return true;
    return false;
  };

  return (
    <div style={{ 
      background: '#fff', 
      borderRadius: '10px', 
      padding: '12px', // Reduced from 16px
      paddingBottom: `calc(80px + env(safe-area-inset-bottom, 80px))`,
      marginBottom: 'calc(20px + env(safe-area-inset-bottom, 20px))'
    }}>
      {/* Exit Confirm Modal (replaces window.confirm for sandbox safety) */}
      {showExitConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
        }} data-testid="soa-exit-confirm-overlay">
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '24px',
            maxWidth: '380px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <div style={{ fontSize: '15px', fontWeight: 600, color: '#333', marginBottom: '12px' }}>
              {language === 'bm' ? 'Kembali ke Step 6?' 
                : language === 'urban' ? 'Go back to Step 6?'
                : 'Go back to Step 6?'}
            </div>
            <div style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
              {language === 'bm'
                ? 'Anda berada di soalan pertama. Adakah anda ingin kembali ke Step 6 (Review & Prime)?'
                : language === 'urban'
                ? 'You are di soalan pertama. Do you want to go back to Step 6 (Review & Prime)?'
                : 'You are at the first question. Do you want to go back to Step 6 (Review & Prime)?'}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                data-testid="soa-exit-cancel-btn"
                onClick={() => setShowExitConfirm(false)}
                style={{
                  flex: 1, padding: '10px', background: '#fff', border: '2px solid #ddd',
                  borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#666', cursor: 'pointer'
                }}
              >
                {language === 'bm' ? 'Batal' : 'Cancel'}
              </button>
              <button
                data-testid="soa-exit-confirm-btn"
                onClick={() => { setShowExitConfirm(false); onBack(); }}
                style={{
                  flex: 1, padding: '10px', background: '#0e67a2', border: 'none',
                  borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: '#fff', cursor: 'pointer'
                }}
              >
                {language === 'bm' ? 'Ya, Kembali' : 'Yes, Go Back'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question Navigation Dots - Always visible for jumping between questions */}
      <div style={{
        display: 'flex', gap: '6px', justifyContent: 'center',
        marginBottom: '10px', padding: '8px 0'
      }} data-testid="soa-question-nav">
        {[0, 1, 2, 3, 4].map((idx) => {
          const isCurrent = idx === currentQ;
          const isComplete = isQuestionComplete(idx);
          const isIncompleteQ = incompleteQuestions.includes(idx);
          const hasAttempt = !!answers[idx];

          let bgColor = '#E0E0E0';
          let borderColor = 'transparent';
          if (isCurrent) { bgColor = '#0e67a2'; borderColor = '#0e67a2'; }
          else if (isIncompleteQ) { bgColor = '#FFCDD2'; borderColor = '#E53935'; }
          else if (isComplete) { bgColor = '#4CAF50'; borderColor = '#4CAF50'; }
          else if (hasAttempt) { bgColor = '#F7941E'; borderColor = '#F7941E'; }

          return (
            <button
              key={idx}
              data-testid={`soa-nav-q${idx + 1}`}
              onClick={() => navigateToQuestion(idx)}
              title={`${language === 'bm' ? 'Soalan' : 'Question'} ${idx + 1}${isIncompleteQ ? (language === 'bm' ? ' - Belum lengkap!' : ' - Incomplete!') : ''}`}
              style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: bgColor, border: `2px solid ${borderColor}`,
                color: (isCurrent || isComplete) ? '#fff' : isIncompleteQ ? '#E53935' : '#666',
                fontSize: '12px', fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease',
                animation: isIncompleteQ ? 'pulse 1.5s infinite' : 'none'
              }}
            >
              {isComplete && !isCurrent ? '\u2713' : idx + 1}
            </button>
          );
        })}
        <style>{`@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }`}</style>
      </div>

      {/* Incomplete Questions Navigation Banner */}
      {incompleteQuestions.length > 0 && (
        <div style={{
          padding: '10px 12px', background: '#FFF3E0', borderRadius: '8px',
          marginBottom: '10px', border: '1px solid #F7941E',
          display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center'
        }} data-testid="soa-incomplete-banner">
          <span style={{ fontSize: '12px', color: '#E65100', fontWeight: 600 }}>
            {language === 'bm' ? 'Soalan belum lengkap:' : 'Incomplete questions:'}
          </span>
          {incompleteQuestions.map(idx => (
            <button
              key={idx}
              data-testid={`soa-goto-incomplete-q${idx + 1}`}
              onClick={() => navigateToQuestion(idx)}
              style={{
                padding: '4px 12px', background: '#E53935', border: 'none',
                borderRadius: '16px', color: '#fff', fontSize: '12px',
                fontWeight: 600, cursor: 'pointer'
              }}
            >
              Q{idx + 1} &rarr;
            </button>
          ))}
        </div>
      )}
      {/* Progress Header - Compact */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '10px', // Reduced from 16px
        padding: '8px 10px', // Reduced from 12px
        background: '#F5F8FF',
        borderRadius: '6px' // Reduced from 8px
      }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}> {/* Reduced from 13px */}
          {language === 'bm' ? 'Soalan' : 'Question'} {currentQ + 1}/5
        </div>
        <div style={{ fontSize: '11px', color: '#666' }}> {/* Reduced from 12px */}
          {language === 'bm' ? 'Cuba' : 'Attempt'} {attempt}/{soaConfig.maxAttempts}
        </div>
        {avgScore !== null && (
          <div style={{ fontSize: '11px', fontWeight: 600, color: avgScore >= soaConfig.passThreshold ? '#4CAF50' : '#F7941E' }}> {/* Reduced from 12px */}
            {language === 'bm' ? 'Purata' : 'Avg'}: {avgScore}
          </div>
        )}
      </div>

      {/* Context Narrative - Compact */}
      {currentQ === 0 && narrative && (
        <div style={{ padding: '8px 10px', background: '#E8F5E9', borderRadius: '6px', marginBottom: '10px', border: '1px solid #4CAF50' }}> {/* Reduced padding */}
          <div style={{ fontSize: '10px', fontWeight: 600, color: '#2E7D32', marginBottom: '3px' }}> {/* Reduced from 11px */}
            💡 {t('cs.steps.soa.yourContext')}
          </div>
          <div style={{ fontSize: '11px', color: '#1B5E20', lineHeight: '1.3' }}> {/* Reduced from 12px and line-height 1.4 */}
            {narrative}
          </div>
        </div>
      )}

      {/* Question - Compact */}
      <div style={{ marginBottom: '12px' }}> {/* Reduced from 16px */}
        <div style={{ fontSize: '15px', fontWeight: 600, color: '#333', marginBottom: '6px' }}> {/* Reduced from 16px and 8px */}
          {getLocalizedQuestion(question)}
        </div>
        <div style={{ fontSize: '10px', color: '#999', marginBottom: '10px' }}> {/* Reduced from 11px and 12px */}
          {t('cs.steps.soa.focus')}: {question.focus.join(', ')}
        </div>

        {/* NEW: Problem Suggestions (Q1 only) */}
        {currentQ === 0 && question.suggested_answers && question.suggested_answers.length > 0 && (
          <div style={{
            background: '#F0F7FF',
            border: '1px solid #3B82F6',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#1E40AF', marginBottom: '8px' }}>
              💡 {language === 'bm' 
                ? 'Cadangan masalah berdasarkan Product Pack:' 
                : 'Suggested problems based on Product Pack:'}
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginBottom: '8px' }}>
              {language === 'bm'
                ? 'Pilih salah satu atau tulis sendiri di bawah'
                : 'Select one or write your own below'}
            </div>
            {question.suggested_answers.map((suggestion, idx) => (
              <button
                key={`item-${idx}`}
                onClick={() => setCurrentAnswer(suggestion.replace(/^[🔴🟡🟢]\s/, ''))} // Remove emoji prefix
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px',
                  marginBottom: '6px',
                  background: currentAnswer === suggestion.replace(/^[🔴🟡🟢]\s/, '') ? '#E0F2FE' : '#fff',
                  border: currentAnswer === suggestion.replace(/^[🔴🟡🟢]\s/, '') ? '2px solid #3B82F6' : '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '12px',
                  color: '#333',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (currentAnswer !== suggestion.replace(/^[🔴🟡🟢]\s/, '')) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#3B82F6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentAnswer !== suggestion.replace(/^[🔴🟡🟢]\s/, '')) {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#ddd';
                  }
                }}
              >
                {suggestion}
              </button>
            ))}
            {question.suggestion_source && (
              <div style={{ fontSize: '10px', color: '#999', marginTop: '6px', fontStyle: 'italic' }}>
                {language === 'bm' 
                  ? `Sumber: ${question.suggestion_source === 'product_benefits' ? 'Benefit Produk' : 'Auto-generated'}` 
                  : `Source: ${question.suggestion_source === 'product_benefits' ? 'Product Benefits' : 'Auto-generated'}`}
              </div>
            )}
          </div>
        )}

        <textarea
          value={currentAnswer}
          onChange={(e) => setCurrentAnswer(e.target.value)}
          placeholder={t('cs.steps.soa.answerPlaceholder')}
          disabled={phase === 'evaluating' || phase === 'finalizing'}
          style={{
            width: '100%',
            padding: '10px', // Reduced from 12px
            border: '1px solid #ddd',
            borderRadius: '6px', // Reduced from 8px
            fontSize: '13px', // Reduced from 14px
            minHeight: '100px', // Reduced from 120px
            fontFamily: 'inherit',
            resize: 'vertical',
            opacity: (phase === 'evaluating' || phase === 'finalizing') ? 0.6 : 1
          }}
        />
      </div>

      {/* AI Coach Feedback - Compact */}
      {phase === 'feedback' && lastEvaluation && (
        <div style={{
          padding: '10px 12px', // Reduced from 16px
          background: lastEvaluation.score >= soaConfig.passThreshold ? '#E8F5E9' : '#FFF3E0',
          borderRadius: '8px', // Reduced from 12px
          marginBottom: '12px', // Reduced from 16px
          border: `2px solid ${lastEvaluation.score >= soaConfig.passThreshold ? '#4CAF50' : '#F7941E'}`,
          animation: 'fadeIn 0.3s ease-in'
        }}>
          <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}> {/* Reduced from 12px */}
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}> {/* Reduced from 14px */}
              💬 {language === 'bm' ? 'AI Coach' : 'AI Coach'}
            </div>
            <div style={{
              padding: '3px 10px', // Reduced from 4px 12px
              borderRadius: '14px', // Reduced from 16px
              fontSize: '13px', // Reduced from 14px
              fontWeight: 700,
              background: lastEvaluation.score >= soaConfig.passThreshold ? '#4CAF50' : lastEvaluation.score >= (soaConfig.passThreshold * 0.75) ? '#F7941E' : '#E53935',
              color: '#fff'
            }}>
              {lastEvaluation.score.toFixed(1)} / 10
            </div>
          </div>

          {/* Parse the feedback string into WHY / HOW / WHAT sections so it
              renders as 3 readable blocks instead of a wall-of-text inline. */}
          {(() => {
            const raw = lastEvaluation.feedback || '';
            // Split on the emoji section markers — works even when the LLM
            // omits the explicit \n\n between sections.
            const markers = [
              { key: 'why',  emoji: '🤔', match: /🤔[^🛠✍]+/u, color: '#F7941E', label: language === 'bm' ? 'WHY — Sebab perlu improve' : 'WHY — Why it needs improving' },
              { key: 'how',  emoji: '🛠️', match: /🛠️[^🤔✍]+/u, color: '#10b981', label: language === 'bm' ? 'HOW — Cara improve' : 'HOW — How to improve' },
              { key: 'what', emoji: '✍️', match: /✍️[^🤔🛠]+/u, color: '#667eea', label: language === 'bm' ? 'WHAT — Apa nak tambah' : 'WHAT — What to add' },
            ];
            const sections = markers
              .map(m => {
                const matched = raw.match(m.match);
                if (!matched) return null;
                // Strip the emoji + leading "WHY/HOW/WHAT this..." label that
                // the LLM tacks on so we don't duplicate the heading.
                const body = matched[0]
                  .replace(/^🤔\s*WHY[^\n:]*:?\s*/iu, '')
                  .replace(/^🛠️\s*HOW[^\n:]*:?\s*/iu, '')
                  .replace(/^✍️\s*WHAT[^\n:]*:?\s*/iu, '')
                  .trim();
                return { ...m, body };
              })
              .filter(Boolean);

            // Fallback: if nothing parsed, render the raw feedback as before
            if (sections.length === 0) {
              return (
                <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.5', marginBottom: '6px', whiteSpace: 'pre-line' }}>
                  {raw}
                </div>
              );
            }

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '6px' }}>
                {sections.map(s => (
                  <div key={s.key} data-testid={`ai-coach-${s.key}`} style={{
                    paddingLeft: '10px',
                    borderLeft: `3px solid ${s.color}`,
                    background: 'rgba(255,255,255,0.55)',
                    borderRadius: '4px',
                    padding: '8px 10px',
                  }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: s.color, marginBottom: '4px', letterSpacing: '0.3px' }}>
                      {s.emoji} {s.label}
                    </div>
                    <div style={{ fontSize: '12.5px', color: '#333', lineHeight: '1.55', whiteSpace: 'pre-line' }}>
                      {s.body}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {lastEvaluation.score < soaConfig.passThreshold && (
            <div style={{ 
              fontSize: '11px', // Reduced from 12px
              color: '#666', 
              fontStyle: 'italic',
              padding: '6px', // Reduced from 8px
              background: 'rgba(255,255,255,0.5)',
              borderRadius: '4px', // Reduced from 6px
              marginTop: '6px' // Reduced from 8px
            }}>
              💡 {lastEvaluation.suggested_improvement}
            </div>
          )}
        </div>
      )}

      {/* AI Rescue Mode - Compact */}
      {phase === 'ai_rescue' && aiRescueData && (
        <div style={{
          padding: '12px 14px', // Reduced from 18px
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '8px', // Reduced from 12px
          marginBottom: '12px', // Reduced from 16px
          border: '2px solid #5a67d8', // Reduced from 3px
          animation: 'fadeIn 0.3s ease-in',
          boxShadow: '0 6px 16px rgba(102, 126, 234, 0.3)' // Slightly reduced
        }}>
          <div style={{ marginBottom: '8px' }}> {/* Reduced from 12px */}
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#fff', marginBottom: '4px' }}> {/* Reduced from 16px and 6px */}
              💡 {language === 'bm' ? 'AI RESCUE MODE' : 'AI RESCUE MODE'}
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.85)', fontStyle: 'italic' }}> {/* Reduced from 11px */}
              {language === 'bm' 
                ? 'Cuba 3 kali belum berjaya. Berikut adalah cadangan AI untuk membantu anda:'
                : '3 attempts not successful. Here\'s an AI suggestion to help you:'}
            </div>
          </div>

          {/* AI Coaching with Why-How-What Framework */}
          <div style={{
            padding: '14px',
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '10px',
            marginBottom: '12px',
            border: '2px solid rgba(255,255,255,0.3)'
          }}>
            {/* WHY - Gaps Analysis (if available) */}
            {aiRescueData.why_gaps_exist && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#f59e0b', marginBottom: '6px' }}>
                  🤔 {language === 'bm' ? 'WHY - Apa Yang Kurang:' : 'WHY - What\'s Missing:'}
                </div>
                <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5', marginBottom: '8px', paddingLeft: '8px', borderLeft: '3px solid #f59e0b' }}>
                  {aiRescueData.why_gaps_exist}
                </div>
              </div>
            )}

            {/* HOW - Strategic Guidance (if available) */}
            {aiRescueData.how_to_strengthen && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#10b981', marginBottom: '6px' }}>
                  🛠️ {language === 'bm' ? 'HOW - Cara Strengthen:' : 'HOW - How to Strengthen:'}
                </div>
                <div style={{ fontSize: '12px', color: '#555', lineHeight: '1.5', marginBottom: '8px', paddingLeft: '8px', borderLeft: '3px solid #10b981' }}>
                  {aiRescueData.how_to_strengthen}
                </div>
              </div>
            )}

            {/* WHAT - Enhanced Answer */}
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: '#667eea', marginBottom: '6px' }}>
                ✨ {language === 'bm' ? 'WHAT - Jawapan Enhanced:' : 'WHAT - Enhanced Answer:'}
              </div>
              <div style={{ fontSize: '13px', color: '#333', lineHeight: '1.6', marginBottom: '8px', paddingLeft: '8px', borderLeft: '3px solid #667eea', background: '#f8fafc', padding: '10px', borderRadius: '6px' }}>
                {aiRescueData.enhanced_answer || aiRescueData.ai_rescue_answer}
              </div>
            </div>

            {/* What Changed (if available) */}
            {aiRescueData.what_changed && (
              <div style={{ 
                fontSize: '11px', 
                color: '#666', 
                fontStyle: 'italic',
                paddingTop: '8px',
                borderTop: '1px solid #e0e0e0'
              }}>
                💭 <strong>{language === 'bm' ? 'Apa Yang Berubah:' : 'What Changed:'}</strong> {aiRescueData.what_changed}
              </div>
            )}
            
            {/* Fallback to old format if new format not available */}
            {!aiRescueData.enhanced_answer && !aiRescueData.why_gaps_exist && aiRescueData.rationale && (
              <div style={{ 
                fontSize: '11px', 
                color: '#666', 
                fontStyle: 'italic',
                paddingTop: '8px',
                borderTop: '1px solid #e0e0e0'
              }}>
                💭 <strong>{language === 'bm' ? 'Kenapa lebih baik:' : 'Why this is better:'}</strong> {aiRescueData.rationale}
              </div>
            )}
          </div>

          {/* AI Rescue Actions */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleAcceptAIRescue}
              style={{
                flex: 2,
                padding: '12px 16px',
                background: '#fff',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                color: '#667eea',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              ✅ {language === 'bm' ? 'Terima AI Rescue (Skor: 8.2 ⚠️ BP -30%)' : language === 'urban' ? 'Accept AI Rescue (Score: 8.2 ⚠️ BP -30%)' : 'Accept AI Rescue (Score: 8.2 ⚠️ BP -30%)'}
            </button>
          </div>

          {/* Warning about required acceptance */}
          <div style={{
            fontSize: '10px',
            color: 'rgba(255,255,255,0.75)',
            marginTop: '10px',
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            🔒 {language === 'bm' 
              ? 'Anda mesti terima AI Rescue untuk teruskan. 3 percubaan telah digunakan.'
              : language === 'urban'
              ? 'You must accept AI Rescue untuk continue. 3 attempts already used.'
              : 'You must accept AI Rescue to continue. 3 attempts already used.'}
          </div>
        </div>
      )}

      {/* Action Buttons - Compact & Mobile Safe */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', // Reduced from 12px
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 60px))',
        marginBottom: '20px',
        marginTop: '12px' // Added top margin instead of relying on bottom margin from above
      }}>
        {phase === 'question' && (
          <>
            <button 
              onClick={handleScopedBack}
              style={{ 
                flex: 1, 
                padding: '12px', // Reduced from 14px
                background: '#fff', 
                border: '2px solid #0e67a2', 
                borderRadius: '8px', // Reduced from 10px
                color: '#0e67a2', 
                fontSize: '13px', // Reduced from 14px
                fontWeight: 600, 
                cursor: 'pointer' 
              }}
            >
              {currentQ > 0 ? (language === 'bm' ? 'Soalan Lepas' : 'Previous Question') : t('cs.buttons.back')}
            </button>
            <button 
              onClick={handleCheckAnswer}
              disabled={!currentAnswer.trim()}
              style={{ 
                flex: 2, 
                padding: '12px', // Reduced from 14px
                background: currentAnswer.trim() ? '#0e67a2' : '#ccc', 
                border: 'none', 
                borderRadius: '8px', // Reduced from 10px
                color: '#fff', 
                fontSize: '13px', // Reduced from 14px
                fontWeight: 600, 
                cursor: currentAnswer.trim() ? 'pointer' : 'not-allowed' 
              }}
            >
              {language === 'bm' ? 'Semak Jawapan' : 'Check Answer'}
            </button>
          </>
        )}

        {/* Feedback removed - evaluating state now handled by global DynamicLoader */}

        {phase === 'feedback' && lastEvaluation && (
          <>
            {/* Check if rescue is required but not accepted */}
            {rescueRequired[currentQ] && !rescueAccepted[currentQ] ? (
              <div style={{ width: '100%' }}>
                <button 
                  disabled
                  style={{ 
                    width: '100%', 
                    padding: '12px', // Reduced from 14px
                    background: '#ccc', 
                    border: 'none', 
                    borderRadius: '8px', // Reduced from 10px
                    color: '#666', 
                    fontSize: '13px', // Reduced from 14px
                    fontWeight: 600, 
                    cursor: 'not-allowed',
                    marginBottom: '6px' // Reduced from 8px
                  }}
                >
                  🔒 {language === 'bm' ? 'Terima AI Rescue Dahulu' : language === 'urban' ? 'Accept AI Rescue Dulu' : 'Accept AI Rescue First'}
                </button>
                <div style={{ 
                  fontSize: '10px', // Reduced from 11px
                  color: '#E53935', 
                  textAlign: 'center',
                  fontStyle: 'italic',
                  marginBottom: '10px'
                }}>
                  {language === 'bm' 
                    ? 'Sila terima cadangan AI untuk teruskan ke soalan seterusnya.'
                    : language === 'urban'
                    ? 'Please accept AI suggestion untuk continue ke next question.'
                    : 'Please accept AI suggestion to continue to the next question.'}
                </div>
                {/* Proceed Anyway option when user is stuck */}
                {attempt >= soaConfig.maxAttempts && (
                  <button 
                    onClick={handleProceedAnyway}
                    style={{ 
                      width: '100%', 
                      padding: '12px',
                      background: '#FF9800', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff', 
                      fontSize: '13px',
                      fontWeight: 600, 
                      cursor: 'pointer',
                      marginTop: '4px'
                    }}
                  >
                    {language === 'bm' 
                      ? '⚠️ Teruskan Tanpa AI Rescue' 
                      : language === 'urban'
                      ? '⚠️ Proceed Without AI Rescue'
                      : '⚠️ Proceed Without AI Rescue'}
                  </button>
                )}
              </div>
            ) : lastEvaluation.can_proceed ? (
              <button 
                onClick={handleNextQuestion}
                style={{ 
                  width: '100%', 
                  padding: '12px', // Reduced from 14px
                  background: '#4CAF50', 
                  border: 'none', 
                  borderRadius: '8px', // Reduced from 10px
                  color: '#fff', 
                  fontSize: '13px', // Reduced from 14px
                  fontWeight: 600, 
                  cursor: 'pointer' 
                }}
              >
                {currentQ < 4 
                  ? (language === 'bm' ? 'Soalan Seterusnya' : 'Next Question')
                  : (language === 'bm' ? 'Selesai' : 'Finish')}
              </button>
            ) : (
              <div style={{ width: '100%' }}>
                <button 
                  onClick={handleRefine}
                  disabled={attempt >= soaConfig.maxAttempts || (rescueRequired[currentQ] && !rescueAccepted[currentQ])}
                  style={{ 
                    width: '100%', 
                    padding: '12px', // Reduced from 14px
                    background: (attempt >= soaConfig.maxAttempts || (rescueRequired[currentQ] && !rescueAccepted[currentQ])) ? '#ccc' : '#F7941E', 
                    border: 'none', 
                    borderRadius: '8px', // Reduced from 10px
                    color: '#fff', 
                    fontSize: '13px', // Reduced from 14px
                    fontWeight: 600, 
                    cursor: (attempt >= soaConfig.maxAttempts || (rescueRequired[currentQ] && !rescueAccepted[currentQ])) ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {language === 'bm' ? 'Perbaiki Jawapan' : 'Refine Answer'}
                </button>
                {/* Show Proceed Anyway when user has exhausted all attempts and score < 8 */}
                {attempt >= soaConfig.maxAttempts && !lastEvaluation.can_proceed && (
                  <button 
                    onClick={handleProceedAnyway}
                    style={{ 
                      width: '100%', 
                      padding: '12px',
                      background: '#FF9800', 
                      border: 'none', 
                      borderRadius: '8px',
                      color: '#fff', 
                      fontSize: '13px',
                      fontWeight: 600, 
                      cursor: 'pointer',
                      marginTop: '8px'
                    }}
                  >
                    {language === 'bm' 
                      ? '⚠️ Teruskan Dengan Skor Semasa' 
                      : language === 'urban'
                      ? '⚠️ Continue With Current Score'
                      : '⚠️ Continue With Current Score'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Retry Button (on error) */}
      {showRetry && phase === 'question' && (
        <button 
          onClick={handleCheckAnswer}
          style={{ 
            width: '100%', 
            marginTop: '12px',
            padding: '12px', 
            background: '#E53935', 
            border: 'none', 
            borderRadius: '8px', 
            color: '#fff', 
            fontSize: '13px', 
            fontWeight: 600, 
            cursor: 'pointer' 
          }}
        >
          {language === 'bm' ? '🔄 Cuba Lagi' : '🔄 Retry'}
        </button>
      )}
    </div>
  );
};

export default Step5SOADrill;
